const REQUEST_TYPE = "REVOLT_DESKTOP_AUTH_REQUEST";
const RESPONSE_TYPE = "REVOLT_DESKTOP_AUTH_RESPONSE";
const REQUEST_TIMEOUT_MS = 8000;

function isEmbeddedRuntime() {
  return typeof window !== "undefined" && window.self !== window.top;
}

function getExpectedParentOrigin() {
  // The embedded site runs inside an Electron-hosted parent document
  // (typically file://, reported as "null" origin), so we cannot target
  // localhost/rvt.dev here. Use wildcard target and rely on request/response
  // type+requestId matching plus parent-side origin allowlist checks.
  return "*";
}

function callBridge(method, params = {}) {
  return new Promise((resolve, reject) => {
    if (!isEmbeddedRuntime()) {
      reject(new Error("Desktop auth bridge unavailable outside embedded runtime"));
      return;
    }

    const requestId = `desktop_auth_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const expectedOrigin = getExpectedParentOrigin() || "*";

    let timeoutId = null;
    const onMessage = (event) => {
      const message = event?.data || {};
      if (message.type !== RESPONSE_TYPE || message.requestId !== requestId) {
        return;
      }

      window.removeEventListener("message", onMessage);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (!message.success) {
        reject(new Error(message.error || "Desktop auth bridge error"));
        return;
      }
      resolve(message.result);
    };

    window.addEventListener("message", onMessage);
    timeoutId = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("Desktop auth bridge request timed out"));
    }, REQUEST_TIMEOUT_MS);

    window.parent.postMessage(
      {
        type: REQUEST_TYPE,
        requestId,
        method,
        params,
      },
      expectedOrigin
    );
  });
}

export function isDesktopBridgeRuntime() {
  return isEmbeddedRuntime();
}

/**
 * Ask the Electron parent to perform desktop token login (`POST /api/account/desktop/login`).
 * For failures, the parent should forward the API JSON body on `result` (including `code`, e.g.
 * `no_local_desktop_entitlement`, and optional `httpStatus`) so the iframe can show the server message
 * and avoid treating entitlement denial like invalid credentials.
 */
export async function desktopBridgeLogin(email, password) {
  return await callBridge("login", { email, password });
}

export async function desktopBridgeStatus() {
  return await callBridge("status");
}

export async function desktopBridgeLogout() {
  return await callBridge("logout");
}

export async function desktopBridgeGetAccessToken() {
  return await callBridge("getAccessToken");
}

export async function desktopBridgeRefreshAccessToken() {
  return await callBridge("refresh");
}
